import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  // Only transparent over the dark hero on the homepage; solid elsewhere for legibility.
  const isTransparent = isHome && !scrolled;
  const textColor = isTransparent ? "text-background-50" : "text-foreground-900";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isTransparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200/70"
      }`}
    >
      <nav className="flex items-center justify-between gap-3 px-4 md:px-6 h-16 md:h-20">
        <Link to="/" className={`flex items-center gap-2.5 cursor-pointer ${textColor}`}>
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-layout-grid-line text-lg"></i>
          </span>
          <span className="font-heading text-lg md:text-xl font-semibold tracking-tight whitespace-nowrap">
            {t("brand")}
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          <Link
            to="/"
            className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100/70 ${textColor}`}
          >
            {t("nav.home")}
          </Link>

          <Link
            to="/basetao-spreadsheet"
            className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100/70 ${textColor}`}
          >
            {t("nav.shoppingGuide")}
          </Link>

          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100/70 ${textColor}`}
            >
              {t("nav.categories")}
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>

            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-[640px] rounded-xl border border-background-200 bg-background-50 p-4 text-foreground-900">
                <div className="grid grid-cols-2 gap-1.5">
                  {categories.map((c) => (
                    <a
                      key={c.key}
                      href={c.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 rounded-lg px-2.5 py-2 cursor-pointer transition-colors hover:bg-background-100"
                    >
                      <img
                        src={c.image}
                        alt={t(`cat.${c.key}`)}
                        className="w-10 h-10 rounded-md object-cover object-top"
                      />
                      <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                        {t(`cat.${c.key}`)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/basetao-legit"
            className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100/70 ${textColor}`}
          >
            {t("nav.readGuide")}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher textColor={textColor} />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            {t("button.buyNow")}
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-background-100/70 ${textColor}`}
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 text-foreground-900 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain">
          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-background-100">
              {t("nav.home")}
            </Link>
            <Link
              to="/basetao-spreadsheet"
              className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-background-100"
            >
              {t("nav.shoppingGuide")}
            </Link>
            <Link to="/basetao-legit" className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-background-100">
              {t("nav.readGuide")}
            </Link>
          </div>

          <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            {t("nav.categories")}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {categories.map((c) => (
              <a
                key={c.key}
                href={c.link}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                <img
                  src={c.image}
                  alt={t(`cat.${c.key}`)}
                  className="w-9 h-9 rounded-md object-cover object-top shrink-0"
                />
                <span className="whitespace-nowrap">{t(`cat.${c.key}`)}</span>
              </a>
            ))}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 flex items-center justify-center gap-1.5 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
          >
            {t("button.buyNow")}
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>
      )}
    </header>
  );
}